import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivitySquare,
  ArrowDownUp,
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  ChevronDown,
  ClipboardList,
  Copy,
  Database,
  Droplets,
  FileUp,
  Gauge,
  Home,
  Layers,
  List,
  ListFilter,
  Menu,
  MessageSquare,
  RefreshCw,
  ScrollText,
  Search,
  SearchCheck,
  Shield,
  ShieldCheck,
  Stethoscope,
  ToggleLeft,
  TrendingDown,
  UserPlus,
  Users,
  UserSearch,
  X,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { UserProfile } from './UserProfile';
import { ThemeToggle } from './ThemeToggle';
import { NavigationBreadcrumb } from './NavigationBreadcrumb';
import { DatabaseStatusBanner } from './DatabaseStatusBanner';
import { BrandBlock } from './BrandBlock';
import {
  shortcutHint,
  SmartSearchDialog,
  useSmartSearchShortcut,
} from './smart-search/SmartSearchDialog.tsx';
import { usePreferences } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { checkPermission, createLogger, Permission } from '@metron/shared';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Separator } from './ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const logger = createLogger('DashboardLayout');

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  icon: React.ElementType;
  label: string;
  path?: string;
  /** Permission required to see this item. Uses the shared Permission enum. */
  requiredPermission?: Permission;
  children?: NavigationItem[];
}

const navigationItems: NavigationItem[] = [
  { icon: Home, label: 'Dashboard', path: '/', requiredPermission: Permission.VIEW_DASHBOARD },
  // Open to every signed-in user; each doc gates itself (see src/docs).
  { icon: BookOpen, label: 'Docs', path: '/docs' },
  {
    icon: Building2,
    label: 'Customer Management',
    requiredPermission: Permission.VIEW_CUSTOMERS,
    children: [
      {
        icon: List,
        label: 'Customer List',
        path: '/customers',
        requiredPermission: Permission.VIEW_CUSTOMERS,
      },
      {
        icon: ClipboardList,
        label: 'Onboarding Queue',
        path: '/customer-onboarding-queue',
        requiredPermission: Permission.VIEW_ONBOARDING_QUEUE,
      },
      {
        icon: ToggleLeft,
        label: 'Activation Status',
        path: '/customer-activation-status',
        requiredPermission: Permission.MANAGE_CUSTOMER_ASSIGNMENTS,
      },
      {
        icon: FileUp,
        label: 'Property Uploads',
        path: '/property-uploads',
        requiredPermission: Permission.VIEW_ALL_PROPERTY_UPLOADS,
      },
      {
        icon: Layers,
        label: 'Custom Groups',
        path: '/custom-groups',
        requiredPermission: Permission.VIEW_CUSTOM_GROUPS,
      },
    ],
  },
  {
    icon: BarChart3,
    label: 'Analytics',
    requiredPermission: Permission.VIEW_METRICS,
    children: [
      {
        icon: Bell,
        label: 'Alert Overview',
        path: '/alert-overview',
        requiredPermission: Permission.VIEW_METRICS,
      },
      {
        icon: MessageSquare,
        label: 'Notification Overview',
        path: '/notification-overview',
        requiredPermission: Permission.VIEW_METRICS,
      },
      {
        icon: ShieldCheck,
        label: 'Read-rate SLA',
        requiredPermission: Permission.VIEW_METRICS,
        children: [
          {
            icon: ShieldCheck,
            label: 'By Customer',
            path: '/read-rate-sla/by-customer',
            requiredPermission: Permission.VIEW_METRICS,
          },
          {
            icon: ShieldCheck,
            label: 'By Product Type',
            path: '/read-rate-sla/by-product-type',
            requiredPermission: Permission.VIEW_METRICS,
          },
        ],
      },
      {
        icon: Droplets,
        label: 'Water Saved',
        path: '/water-saved',
        requiredPermission: Permission.VIEW_METRICS,
      },
    ],
  },
  {
    icon: Stethoscope,
    label: 'Diagnostics',
    // Every role with VIEW_METER_INSPECTOR also has VIEW_METRICS, so gating the
    // group on VIEW_METRICS keeps the header from rendering empty.
    requiredPermission: Permission.VIEW_METRICS,
    children: [
      {
        icon: ActivitySquare,
        label: 'Ingest Statistics',
        path: '/ingest-statistics',
        requiredPermission: Permission.VIEW_METRICS,
      },
      {
        icon: Database,
        label: 'UDP Logs',
        path: '/udp-logs',
        requiredPermission: Permission.VIEW_METRICS,
      },
      {
        icon: Copy,
        label: 'Duplicate Messages',
        path: '/duplicate-messages',
        requiredPermission: Permission.VIEW_METRICS,
      },
      {
        icon: Gauge,
        label: 'Max Flow',
        path: '/max-flow',
        requiredPermission: Permission.VIEW_METRICS,
      },
      {
        icon: ArrowDownUp,
        label: 'Backflow',
        requiredPermission: Permission.VIEW_METRICS,
        children: [
          {
            icon: TrendingDown,
            label: 'Most Negative Avg Use',
            path: '/backflow/most-negative-avg-use',
            requiredPermission: Permission.VIEW_METRICS,
          },
          {
            icon: SearchCheck,
            label: 'Largest Classified',
            path: '/backflow/largest-classified',
            requiredPermission: Permission.VIEW_METRICS,
          },
        ],
      },
      {
        icon: Search,
        label: 'Meter Inspector',
        path: '/meter-inspector',
        requiredPermission: Permission.VIEW_METER_INSPECTOR,
      },
      {
        icon: ListFilter,
        label: 'Meter Search',
        path: '/search/meters',
        requiredPermission: Permission.VIEW_METER_INSPECTOR,
      },
      {
        icon: Building2,
        label: 'Customer Search',
        path: '/search/customers',
        requiredPermission: Permission.VIEW_CUSTOMERS,
      },
      {
        icon: Home,
        label: 'Property Search',
        path: '/search/properties',
        requiredPermission: Permission.VIEW_CUSTOMERS,
      },
      {
        icon: Layers,
        label: 'Custom Group Search',
        path: '/search/custom-groups',
        requiredPermission: Permission.VIEW_CUSTOM_GROUPS,
      },
      {
        icon: Users,
        label: 'End Consumer Search',
        path: '/search/end-consumers',
        requiredPermission: Permission.MANAGE_END_CONSUMERS,
      },
      {
        icon: UserSearch,
        label: 'User Search',
        path: '/search/users',
        requiredPermission: Permission.VIEW_USERS,
      },
    ],
  },
  {
    icon: BarChart3,
    label: 'Reports',
    path: '/reports',
    requiredPermission: Permission.VIEW_METER_INSPECTOR,
  },
  {
    icon: Users,
    label: 'Access Management',
    requiredPermission: Permission.VIEW_USERS,
    children: [
      {
        icon: List,
        label: 'Plus Users',
        path: '/users',
        requiredPermission: Permission.VIEW_USERS,
      },
      {
        icon: UserPlus,
        label: 'Datalink Users',
        path: '/datalink/accounts',
        requiredPermission: Permission.VIEW_DATALINK_ACCOUNTS,
      },
    ],
  },
  {
    icon: ToggleLeft,
    label: 'Feature Flags',
    path: '/feature-flags',
    requiredPermission: Permission.VIEW_FEATURE_FLAGS,
  },
  {
    icon: ScrollText,
    label: 'Audit Logs',
    path: '/audit-logs',
    requiredPermission: Permission.VIEW_AUDIT_LOGS,
  },
  // Wellspring's own administration (as opposed to managing Plus customers/users). Admin and up.
  {
    icon: Shield,
    label: 'Admin',
    requiredPermission: Permission.MANAGE_WELLSPRING_ROLES,
    children: [
      {
        icon: ShieldCheck,
        label: 'Wellspring Access',
        path: '/wellspring-access',
        requiredPermission: Permission.MANAGE_WELLSPRING_ROLES,
      },
      {
        icon: RefreshCw,
        label: 'Metrics Refresh',
        path: '/admin/metrics/refresh',
        requiredPermission: Permission.MANAGE_WELLSPRING_ROLES,
      },
    ],
  },
];

const COLLAPSED_SECTIONS_KEY = 'sidebarCollapsedSections';

/** Collect labels of all navigation items that have children (i.e. can be collapsed) */
function getCollapsibleLabels(items: NavigationItem[]): Set<string> {
  const labels = new Set<string>();
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      labels.add(item.label);
      for (const label of getCollapsibleLabels(item.children)) {
        labels.add(label);
      }
    }
  }
  return labels;
}

/** Load collapsed sections from localStorage, discarding any labels that no longer exist in navigation */
function loadCollapsedSections(validLabels: Set<string>): Set<string> {
  try {
    const stored = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(
          parsed.filter((label): label is string =>
            typeof label === 'string' && validLabels.has(label)
          ),
        );
      }
    }
  } catch {
    // Corrupted data — start fresh
  }
  return new Set<string>();
}

function saveCollapsedSections(sections: Set<string>): void {
  try {
    localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify([...sections]));
  } catch {
    // Storage full or unavailable — ignore
  }
}

/** Check if any descendant has a path matching the current location */
function hasActiveDescendant(item: NavigationItem, pathname: string): boolean {
  if (item.children) {
    return item.children.some(
      (child) => child.path === pathname || hasActiveDescendant(child, pathname),
    );
  }
  return false;
}

/** Recursive nav item for expanded sidebar and mobile views */
function NavItemExpanded({
  item,
  depth,
  hasAccess,
  pathname,
  collapsedSections,
  toggleSection,
  onNavigate,
}: {
  item: NavigationItem;
  depth: number;
  hasAccess: (item: NavigationItem) => boolean;
  pathname: string;
  collapsedSections: Set<string>;
  toggleSection: (label: string) => void;
  onNavigate?: () => void;
}) {
  const accessibleChildren = item.children?.filter(hasAccess) ?? [];
  const hasChildren = accessibleChildren.length > 0;
  const isActive = item.path === pathname;
  const isAncestorActive = hasChildren && hasActiveDescendant(item, pathname);
  const isCollapsed = collapsedSections.has(item.label);

  const chevron = hasChildren && (
    <ChevronDown
      className={`h-3 w-3 shrink-0 transition-transform duration-200 ${
        isCollapsed ? '-rotate-90' : ''
      }`}
    />
  );

  const childrenContent = hasChildren && !isCollapsed && (
    <div className='ml-4 space-y-0.5'>
      {accessibleChildren.map((child) => (
        <NavItemExpanded
          key={child.label}
          item={child}
          depth={depth + 1}
          hasAccess={hasAccess}
          pathname={pathname}
          collapsedSections={collapsedSections}
          toggleSection={toggleSection}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );

  // Leaf link: has path, no children
  if (item.path && !hasChildren) {
    return (
      <Button
        variant={isActive ? 'secondary' : 'ghost'}
        className='w-full justify-start px-2'
        size={depth === 0 ? 'default' : 'sm'}
        asChild
        onClick={onNavigate}
      >
        <Link to={item.path}>
          <item.icon className='mr-2 h-4 w-4 shrink-0' />
          {item.label}
        </Link>
      </Button>
    );
  }

  // Link with children: has both path and children
  if (item.path && hasChildren) {
    return (
      <div>
        <div className='flex items-center gap-0.5'>
          <Button
            variant={isActive || isAncestorActive ? 'secondary' : 'ghost'}
            className='flex-1 justify-start min-w-0 px-2'
            size={depth === 0 ? 'default' : 'sm'}
            asChild
            onClick={onNavigate}
          >
            <Link to={item.path}>
              <item.icon className='mr-2 h-4 w-4 shrink-0' />
              <span className='truncate'>{item.label}</span>
            </Link>
          </Button>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8 shrink-0'
            onClick={() => toggleSection(item.label)}
          >
            {chevron}
          </Button>
        </div>
        {childrenContent}
      </div>
    );
  }

  // Section header — uppercase Titillium reads as a brand-aligned section
  // eyebrow rather than another link.
  return (
    <div>
      <button
        type='button'
        className={`flex w-full items-center rounded-md px-2 py-2 font-heading text-xs font-semibold uppercase tracking-wider transition-colors ${
          isAncestorActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
        }`}
        onClick={() => hasChildren && toggleSection(item.label)}
      >
        <item.icon className='mr-2 h-4 w-4 shrink-0' />
        <span className='flex-1 text-left truncate'>{item.label}</span>
        {chevron}
      </button>
      {childrenContent}
    </div>
  );
}

/** Recursive dropdown menu items for collapsed sidebar flyouts */
function CollapsedDropdownItems({
  items,
  hasAccess,
}: {
  items: NavigationItem[];
  hasAccess: (item: NavigationItem) => boolean;
}) {
  return (
    <>
      {items.filter(hasAccess).map((child) => {
        const accessibleGrandchildren = child.children?.filter(hasAccess) ?? [];

        if (accessibleGrandchildren.length > 0) {
          return (
            <DropdownMenuSub key={child.label}>
              <DropdownMenuSubTrigger>
                <child.icon className='mr-2 h-4 w-4' />
                {child.label}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {child.path && (
                  <DropdownMenuItem asChild>
                    <Link to={child.path}>
                      <child.icon className='mr-2 h-4 w-4' />
                      {child.label}
                    </Link>
                  </DropdownMenuItem>
                )}
                <CollapsedDropdownItems
                  items={accessibleGrandchildren}
                  hasAccess={hasAccess}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          );
        }

        if (child.path) {
          return (
            <DropdownMenuItem key={child.label} asChild>
              <Link to={child.path}>
                <child.icon className='mr-2 h-4 w-4' />
                {child.label}
              </Link>
            </DropdownMenuItem>
          );
        }

        return null;
      })}
    </>
  );
}

/** Collapsed sidebar item: icon button with dropdown flyout for children */
function NavItemCollapsed({
  item,
  hasAccess,
  pathname,
}: {
  item: NavigationItem;
  hasAccess: (item: NavigationItem) => boolean;
  pathname: string;
}) {
  const accessibleChildren = item.children?.filter(hasAccess) ?? [];
  const hasChildren = accessibleChildren.length > 0;
  const isActive = item.path === pathname;
  const isAncestorActive = hasChildren && hasActiveDescendant(item, pathname);

  // Leaf link: icon button
  if (item.path && !hasChildren) {
    return (
      <Button
        variant={isActive ? 'secondary' : 'ghost'}
        className='w-full justify-center px-2'
        size='icon'
        asChild
      >
        <Link to={item.path}>
          <item.icon className='h-4 w-4' />
        </Link>
      </Button>
    );
  }

  // Has children: dropdown flyout
  if (hasChildren) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={isActive || isAncestorActive ? 'secondary' : 'ghost'}
            className='w-full justify-center px-2'
            size='icon'
          >
            <item.icon className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side='right' align='start'>
          <DropdownMenuLabel>{item.label}</DropdownMenuLabel>
          {item.path && (
            <DropdownMenuItem asChild>
              <Link to={item.path}>
                <item.icon className='mr-2 h-4 w-4' />
                Overview
              </Link>
            </DropdownMenuItem>
          )}
          <CollapsedDropdownItems
            items={accessibleChildren}
            hasAccess={hasAccess}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // No path, no children: nothing to render in collapsed mode
  return null;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { preferences, updatePreferences } = usePreferences();
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const collapsibleLabels = useMemo(() => getCollapsibleLabels(navigationItems), []);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => loadCollapsedSections(collapsibleLabels),
  );

  const userGroups = user?.groups ?? [];

  // Smart Search: one dialog for both headers, opened by button or ⌘K / Ctrl+K.
  const canSmartSearch = checkPermission(userGroups, Permission.VIEW_METER_INSPECTOR);
  const [searchOpen, setSearchOpen] = useState(false);
  useSmartSearchShortcut(canSmartSearch, setSearchOpen);
  const smartSearchButton = canSmartSearch && (
    <Button
      variant='outline'
      size='sm'
      className='gap-2 text-muted-foreground'
      onClick={() => setSearchOpen(true)}
      aria-label='Open Smart Search'
    >
      <Search className='h-4 w-4' aria-hidden='true' />
      <span className='hidden sm:inline'>Search</span>
      <kbd className='hidden sm:inline text-xs border rounded px-1'>{shortcutHint()}</kbd>
    </Button>
  );

  // Helper function to check if user has access to a navigation item
  const hasAccess = (item: NavigationItem): boolean => {
    if (item.requiredPermission) {
      return checkPermission(userGroups, item.requiredPermission);
    }
    // No restrictions, allow access
    return true;
  };

  const visibleNavItems = navigationItems.filter((item) => hasAccess(item));

  const toggleSidebar = async () => {
    try {
      await updatePreferences({
        sidebarCollapsed: !preferences.sidebarCollapsed,
      });
    } catch (error) {
      logger.error({ error }, 'Failed to toggle sidebar');
    }
  };

  const toggleSection = useCallback((label: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      saveCollapsedSections(next);
      return next;
    });
  }, []);

  return (
    <div className='min-h-screen bg-background'>
      {/* Mobile header */}
      <div className='lg:hidden flex items-center justify-between p-4 border-b'>
        <div className='flex items-center gap-2'>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant='ghost' size='icon'>
                <Menu className='h-5 w-5' />
              </Button>
            </SheetTrigger>
            <SheetContent side='left' className='w-[280px] sm:w-[350px] flex flex-col'>
              <SheetHeader className='text-left'>
                <SheetTitle className='sr-only'>Wellspring navigation</SheetTitle>
                <BrandBlock />
              </SheetHeader>
              <Separator className='my-4' />
              <nav className='flex-1 min-h-0 overflow-y-auto scrollbar-hidden space-y-2'>
                {visibleNavItems.map((item) => (
                  <NavItemExpanded
                    key={item.label}
                    item={item}
                    depth={0}
                    hasAccess={hasAccess}
                    pathname={location.pathname}
                    collapsedSections={collapsedSections}
                    toggleSection={toggleSection}
                    onNavigate={() => setMobileOpen(false)}
                  />
                ))}
              </nav>
              <Separator className='my-4' />
              <div className='space-y-2'>
                <div className='flex items-center justify-between px-2'>
                  <span className='text-sm font-medium'>Theme</span>
                  <ThemeToggle />
                </div>
              </div>
            </SheetContent>
          </Sheet>
          <BrandBlock />
        </div>
        <div className='flex items-center gap-2'>
          {smartSearchButton}
          <UserProfile />
        </div>
      </div>
      {canSmartSearch && <SmartSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />}

      <div className='flex'>
        {/* Sidebar */}
        <aside
          className={`
            ${preferences.sidebarCollapsed ? 'w-16' : 'w-64'}
            hidden lg:flex flex-col bg-card border-r transition-all duration-300
            h-screen sticky top-0 z-10 overflow-hidden
          `}
        >
          {/* Sidebar header — brand block anchors Wellspring to the Metron mark */}
          <div
            className={`flex items-center border-b ${
              preferences.sidebarCollapsed ? 'flex-col gap-3 p-3' : 'justify-between p-4'
            }`}
          >
            <BrandBlock compact={preferences.sidebarCollapsed} />
            <Button
              variant='ghost'
              size='icon'
              onClick={toggleSidebar}
              aria-label={preferences.sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {preferences.sidebarCollapsed
                ? <Menu className='h-4 w-4' />
                : <X className='h-4 w-4' />}
            </Button>
          </div>

          {/* Navigation items */}
          <nav className='flex-1 min-h-0 overflow-y-auto scrollbar-hidden p-4'>
            <div className='space-y-2'>
              {visibleNavItems.map((item) => (
                <div key={item.label}>
                  {preferences.sidebarCollapsed
                    ? (
                      <NavItemCollapsed
                        item={item}
                        hasAccess={hasAccess}
                        pathname={location.pathname}
                      />
                    )
                    : (
                      <NavItemExpanded
                        item={item}
                        depth={0}
                        hasAccess={hasAccess}
                        pathname={location.pathname}
                        collapsedSections={collapsedSections}
                        toggleSection={toggleSection}
                      />
                    )}
                </div>
              ))}
            </div>
          </nav>

          {/* Sidebar footer */}
          <Separator />
          <div className='p-4'>
            <div
              className={`flex items-center gap-2 ${
                preferences.sidebarCollapsed ? 'justify-center flex-col' : ''
              }`}
            >
              <ThemeToggle />
              {!preferences.sidebarCollapsed && <UserProfile />}
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className='flex-1 min-w-0 min-h-screen overflow-x-hidden'>
          {
            /* Desktop header — Metron eyebrow over a Titillium product mark.
              Per the brand guide, "Headings: Title Case" so the product name
              reads as the surface itself; secondary context lives in muted
              copy beneath. */
          }
          <header className='hidden lg:flex items-center justify-between gap-6 px-8 py-5 border-b bg-card'>
            <div className='min-w-0'>
              <p className='metron-eyebrow'>Metron Water Intelligence</p>
              <h2 className='font-heading text-2xl font-normal tracking-tight mt-1'>
                Wellspring
              </h2>
            </div>
            <div className='flex items-center gap-3'>
              {smartSearchButton}
              <ThemeToggle />
              <UserProfile />
            </div>
          </header>

          {/* Content area */}
          <div className='p-6'>
            <NavigationBreadcrumb />
            <DatabaseStatusBanner />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
